// PDF export — Radio Apps branded, one clock-page per page, filtered
// to non-empty clocks, with cover + branding + watermark.
//
// The DOM to be rasterized is prepared by <ExportView/> which renders
// each PDF page inside a `[data-pdf-page]` block. We simply walk those
// blocks in document order.

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const A4_LANDSCAPE_W = 297;   // mm
const A4_LANDSCAPE_H = 210;   // mm

async function rasterize(node) {
  const canvas = await html2canvas(node, {
    backgroundColor: '#ffffff',
    scale: 1.6,
    useCORS: true,
    logging: false,
    foreignObjectRendering: false,
    windowWidth: Math.max(node.scrollWidth, 1400),
    onclone: (doc) => {
      // Unhide any hidden ancestors so the node is truly painted.
      doc.querySelectorAll('[hidden]').forEach((el) => el.removeAttribute('hidden'));
      // Ensure our exportview positioning is undone in the clone so
      // html2canvas can measure it.
      const cloned = doc.querySelector('[data-pdf-page]');
      if (cloned) {
        let p = cloned.parentElement;
        while (p) {
          p.style.position = 'static';
          p.style.left = '';
          p.style.top = '';
          p = p.parentElement;
        }
      }
    },
  });
  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.85),
    width: canvas.width,
    height: canvas.height,
  };
}

function drawFooter(pdf) {
  // Just a clean footer line — no watermark, no email, no timestamp.
  pdf.setTextColor(120, 120, 120);
  pdf.setFontSize(9);
  pdf.text('© Radio Solutions · Radio Apps', 8, A4_LANDSCAPE_H - 4);
  pdf.text('www.radio-apps.online', A4_LANDSCAPE_W - 8, A4_LANDSCAPE_H - 4, { align: 'right' });
}

/**
 * Export the pages rendered by <ExportView/> to a branded PDF.
 * @param {HTMLElement} rootEl the container element holding [data-pdf-page] children
 * @param {{ templateName?: string }} opts
 */
export async function exportBuilderToPdf(rootEl, opts = {}) {
  if (!rootEl) throw new Error('missing export root');
  const pages = Array.from(rootEl.querySelectorAll('[data-pdf-page]'));
  if (pages.length === 0) throw new Error('no pages to export');

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    if (i > 0) pdf.addPage('a4', 'landscape');
    const img = await rasterize(page);

    const ratio = img.width / img.height;
    let w = A4_LANDSCAPE_W;
    let h = A4_LANDSCAPE_W / ratio;
    if (h > A4_LANDSCAPE_H) {
      h = A4_LANDSCAPE_H;
      w = A4_LANDSCAPE_H * ratio;
    }
    const x = (A4_LANDSCAPE_W - w) / 2;
    const y = (A4_LANDSCAPE_H - h) / 2;
    pdf.addImage(img.dataUrl, 'JPEG', x, y, w, h);

    // Cover has its own branded footer baked in — do not overlap it.
    const isCover = page.getAttribute('data-pdf-page') === 'cover';
    if (!isCover) {
      drawFooter(pdf);
    }
  }

  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safe = (opts.templateName || 'radio_apps_clock').replace(/[^\w\-]+/g, '_');
  pdf.save(`${safe}_${stamp}.pdf`);
}
